import { useInfiniteQuery } from '@tanstack/react-query';

import {
  sharePointSearchQueryOptions,
  type SharePointSearchHit,
} from '@/lib/api/sharepoint.js';

import { useBrowseList, type BrowseList } from './browse-levels.js';

/**
 * How long a search box waits for the typing to settle. The query is a call to Microsoft Search, so
 * without this every keystroke is a round trip that replaces the list with a skeleton — the same reason
 * the knowledge picker debounces its site search, and the same quarter second.
 *
 * It lives beside the hook rather than in each box: the tree's own search and the surface-wide fan have to
 * feel like one feature, and two constants would be two features the day somebody tuned one of them.
 */
export const SEARCH_DEBOUNCE_MS = 250;

/**
 * Files matching a query, from everywhere the workspace's SharePoint connection reaches — by name and by
 * contents, because it is Microsoft Search and not a filter over the rows already on screen.
 *
 * It is flattened by the same {@link useBrowseList} the tree's levels use, so the results page with the
 * same "Show more" and end on the same rule. What differs is underneath: searching pages by a numeric
 * offset, so the tree's cursor pager cannot be pointed at it.
 *
 * An empty query runs nothing. The service answers 422 to a blank `q`, and an empty search box means
 * "show me the tree" rather than "search for nothing".
 */
export function useSharePointFileSearch(
  workspaceId: string,
  query: string,
  /**
   * Which connection to search. `null` leaves it to the service, which searches the workspace's shared
   * connection. It is part of the cache key, so switching it never serves the other tenant's hits.
   */
  connectionId: string | null = null,
): BrowseList<SharePointSearchHit> {
  return useBrowseList(
    useInfiniteQuery(
      sharePointSearchQueryOptions(workspaceId, connectionId, query),
    ),
  );
}
